import { useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import type { Swiper as SwiperClass } from 'swiper'
import 'swiper/css'
import type { Banner } from '@kcm/contracts'

/**
 * The marketing hero slider (PLAN.md §9), shown on the sign-in page only.
 *
 * The image is full-bleed at its own aspect ratio — never cropped and never
 * letterboxed — because a marketing banner is designed as one piece and carries
 * its own background. Beneath it sits a row of gold bars, the single brand
 * accent (§9.1), rendered ourselves rather than via Swiper's dots so they can
 * be tokened and shaped freely.
 */
export function BannerSlider({ banners }: { banners: Banner[] }) {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null)
  const [active, setActive] = useState(0)

  if (banners.length === 0) return null

  return (
    <div className="flex flex-col items-center gap-3">
      <Swiper
        modules={[Autoplay]}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop={banners.length > 1}
        onSwiper={setSwiper}
        onSlideChange={(s) => setActive(s.realIndex)}
        className="w-full overflow-hidden rounded-xl"
      >
        {banners.map((banner, i) => (
          <SwiperSlide key={`${banner.imageUrl}-${i}`}>
            <Slide banner={banner} />
          </SwiperSlide>
        ))}
      </Swiper>

      {banners.length > 1 && (
        <div className="flex items-center justify-center gap-2">
          {banners.map((banner, i) => (
            <button
              key={`${banner.imageUrl}-${i}`}
              type="button"
              aria-label={`Show slide ${i + 1} of ${banners.length}`}
              aria-current={i === active}
              onClick={() => swiper?.slideToLoop(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === active ? 'w-8 bg-accent' : 'w-4 bg-line hover:bg-muted-soft'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function Slide({ banner }: { banner: Banner }) {
  const [loaded, setLoaded] = useState(false)

  const image = (
    <img
      src={banner.imageUrl}
      alt={banner.alt ?? ''}
      onLoad={() => setLoaded(true)}
      // Full width at the image's own height: no crop, no letterbox bars.
      className={`block h-auto w-full transition-opacity duration-500 ${
        loaded ? 'opacity-100' : 'opacity-0'
      }`}
    />
  )

  return banner.linkUrl ? (
    <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" className="block">
      {image}
    </a>
  ) : (
    image
  )
}
